import { useEffect } from 'react';
import { AudioLines, MessageSquare, Moon, Sun } from 'lucide-react';
import { CreditsMeter } from '@/components/layout/CreditsMeter';
import { AvatarMenu } from '@/components/layout/AvatarMenu';
import type { Slide } from '@/stores/videoWorkspaceStore';
import { ProjectApps } from './ProjectApps';
import { VideoCreditsUsed } from './VideoCreditsUsed';
import { VideoPublishButton } from './VideoPublishButton';

interface PhoneMoreSheetBProps {
  conversationId: string;
  hasOutline: boolean;
  showPublish: boolean;
  slides: Slide[];
  defaultShareTab: 'blog' | 'video';
  audioAvailable: boolean;
  audioIsScripts: boolean;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  onAudio: () => void;
  onReviews: () => void;
  onClose: () => void;
  /** Over the storyboard: where it draws its page menu, which the phone's bar has no room for. */
  pageSlotRef?: (el: HTMLDivElement | null) => void;
}

const ROW = 'flex h-11 w-full items-center gap-3 rounded-[var(--b-radius-control)] px-3 text-left text-b-control text-b-t1 hover:bg-b-raised';

/**
 * Direction B on a phone (D9): what the desktop bar holds beyond the view and the progress, in a sheet from the foot:
 * the storyboard's pages, Publish, Audio, Reviews, day/night, Apps and the account. It sits over Ivy's peek and leaves
 * it as it was.
 */
export function PhoneMoreSheetB(props: PhoneMoreSheetBProps) {
  const { conversationId, hasOutline, showPublish, slides, defaultShareTab, audioAvailable, audioIsScripts, isDarkMode, onToggleTheme, onAudio, onReviews, onClose, pageSlotRef } = props;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const pick = (act: () => void) => () => { onClose(); act(); };
  return (
    <>
      <button type="button" data-more-scrim aria-label="Close" tabIndex={-1} className="fixed inset-0 z-40 cursor-default bg-black/40" onClick={onClose} />
      <div
        data-more-sheet
        data-b-surface=""
        role="dialog"
        aria-label="More"
        className="fixed inset-x-0 bottom-0 z-50 flex flex-col gap-1 rounded-t-[var(--b-radius-sheet)] border-t border-b-line bg-b-panel px-3 pb-[calc(12px+env(safe-area-inset-bottom))] pt-2 shadow-[0_-10px_30px_-12px_rgb(0_0_0/0.3)]"
      >
        <span aria-hidden className="mx-auto mb-2 h-1 w-9 rounded-full bg-b-line-strong" />
        {pageSlotRef && (
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-1 has-[[data-more-slot]:empty]:hidden">
            <span className="text-b-control text-b-t1">Storyboard page</span>
            <div data-more-slot ref={pageSlotRef} className="flex items-center" />
          </div>
        )}
        {showPublish && (
          <div className="flex items-center justify-between px-3 py-1">
            <span className="text-b-control text-b-t1">Publish</span>
            <VideoPublishButton
              conversationId={conversationId}
              slideCount={slides.length}
              allSlidesReady={slides.every((s) => s.status === 'completed')}
              slides={slides.map((s) => ({ id: s.id, title: s.title }))}
              defaultShareTab={defaultShareTab}
              compact
            />
          </div>
        )}
        {audioAvailable && (
          <button type="button" className={ROW} onClick={pick(onAudio)}>
            <AudioLines className="h-[18px] w-[18px] text-b-t3" />
            {audioIsScripts ? 'Scripts' : 'Audio & script'}
          </button>
        )}
        {hasOutline && (
          <button type="button" className={ROW} onClick={pick(onReviews)}>
            <MessageSquare className="h-[18px] w-[18px] text-b-t3" />
            Reviews
          </button>
        )}
        <button type="button" className={ROW} onClick={onToggleTheme}>
          {isDarkMode ? <Sun className="h-[18px] w-[18px] text-b-t3" /> : <Moon className="h-[18px] w-[18px] text-b-t3" />}
          {isDarkMode ? 'Light mode' : 'Dark mode'}
        </button>
        <div className="flex items-center justify-between px-3 py-1">
          <span className="text-b-control text-b-t1">Apps</span>
          <ProjectApps projectId={conversationId} trigger="icon" />
        </div>
        <div className="flex items-center justify-between px-3 py-1">
          <span className="text-b-control text-b-t1">Account</span>
          <AvatarMenu
            compact
            extra={
              <div className="grid gap-1.5">
                <CreditsMeter variant="pill" />
                {hasOutline && <VideoCreditsUsed conversationId={conversationId} />}
              </div>
            }
          />
        </div>
      </div>
    </>
  );
}
